import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { orderAPI } from '../services/api';
import PaymentSuccess from './PaymentSuccess';

const PaymentStatus = () => {
    const [loading, setLoading] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [bookingDetails, setBookingDetails] = useState(null);
    const [error, setError] = useState(null);
    const [checkingAttempt, setCheckingAttempt] = useState(1);
    
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const checkPaymentStatus = async () => {
            try {
                // Get transaction ID from URL params
                const params = new URLSearchParams(location.search);
                const transactionId = params.get('transactionId');

                if (!transactionId) {
                    throw new Error('Transaction ID not found');
                }

                // Get pending booking details from sessionStorage
                const pendingBookingStr = sessionStorage.getItem('pendingBooking');
                if (!pendingBookingStr) {
                    throw new Error('Booking details not found. Please contact support.');
                }

                const pendingBooking = JSON.parse(pendingBookingStr);

                // Try to verify payment status multiple times
                // PhonePe status might not be immediately available
                let verifyResult;
                let attempts = 0;
                const maxAttempts = 5;
                const delayMs = 2000; // 2 seconds between attempts

                while (attempts < maxAttempts) {
                    attempts++;
                    setCheckingAttempt(attempts);
                    console.log(`Checking payment status, attempt ${attempts}/${maxAttempts}`);
                    
                    // Verify payment status with backend
                    verifyResult = await orderAPI.verifyPayment({ transactionId });
                    
                    // If payment is successful, break out of loop
                    if (verifyResult.success && (verifyResult.status === 'paid' || verifyResult.status === 'confirmed')) {
                        break;
                    }
                    
                    // If payment failed or cancelled, stop trying
                    if (verifyResult.status === 'failed' || verifyResult.status === 'cancelled') {
                        break;
                    }
                    
                    // If not the last attempt, wait before trying again
                    if (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, delayMs));
                    }
                }

                // Now process the final result
                if (verifyResult.success && (verifyResult.status === 'paid' || verifyResult.status === 'confirmed')) {
                    // Payment successful
                    setPaymentStatus('success');
                    setBookingDetails({
                        ...verifyResult.booking,
                        ...pendingBooking,
                        selectedDate: new Date(pendingBooking.selectedDate)
                    });
                    
                    // Clear pending booking from sessionStorage
                    sessionStorage.removeItem('pendingBooking');
                } else if (verifyResult.status === 'failed' || verifyResult.status === 'cancelled') {
                    // Payment failed or cancelled
                    setPaymentStatus('failed');
                    setError(verifyResult.message || 'Payment was not successful');
                    
                    // Clear pending booking
                    sessionStorage.removeItem('pendingBooking');
                } else {
                    // Payment still pending or not found
                    setPaymentStatus('pending');
                    
                    // Check the actual message from backend
                    if (verifyResult.message && verifyResult.message.includes('not found')) {
                        setError('Payment was not completed. The transaction may have been cancelled or expired.');
                    } else {
                        setError('Payment is still being processed. Please check back later.');
                    }
                }

            } catch (err) {
                console.error('Error checking payment status:', err);
                setError(err.message || 'Failed to verify payment status');
                setPaymentStatus('error');
            } finally {
                setLoading(false);
            }
        };

        checkPaymentStatus();
    }, [location]);

    const handleRetry = () => {
        navigate('/');
        window.location.hash = '#booking';
    };

    const handleHome = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-xl">Verifying payment status...</p>
                    <p className="text-sm text-gray-400 mt-2">
                        {checkingAttempt > 1 && `Checking... (Attempt ${checkingAttempt}/5)`}
                    </p>
                    <p className="text-xs text-gray-500 mt-4">
                        Please wait while we confirm your payment
                    </p>
                </div>
            </div>
        );
    }

    if (paymentStatus === 'success' && bookingDetails) {
        return <PaymentSuccess bookingDetails={bookingDetails} onClose={() => navigate('/')} />;
    }

    if (paymentStatus === 'failed' || paymentStatus === 'error') {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <div className="mb-8">
                        <div className="mx-auto h-20 w-20 bg-red-500 rounded-full flex items-center justify-center mb-4">
                            <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
                        <p className="text-gray-400">{error}</p>
                    </div>
                    
                    <div className="space-y-3">
                        <button
                            onClick={handleRetry}
                            className="w-full px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            Try Again
                        </button>
                        <button
                            onClick={handleHome}
                            className="w-full px-6 py-3 border border-white text-white rounded-lg hover:bg-white hover:text-black transition-colors"
                        >
                            Go to Home
                        </button>
                    </div>

                    <p className="text-sm text-gray-500 mt-6">
                        If you continue to face issues, please contact our support team.
                    </p>
                </div>
            </div>
        );
    }

    if (paymentStatus === 'pending') {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <div className="mb-8">
                        <div className="mx-auto h-20 w-20 bg-yellow-500 rounded-full flex items-center justify-center mb-4">
                            <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold mb-2">Payment Pending</h2>
                        <p className="text-gray-400">{error}</p>
                    </div>
                    
                    <button
                        onClick={handleHome}
                        className="w-full px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Go to Home
                    </button>

                    <p className="text-sm text-gray-500 mt-6">
                        We'll send you an email confirmation once the payment is processed.
                    </p>
                </div>
            </div>
        );
    }

    return null;
};

export default PaymentStatus;